/**
 * Access Health: the /admin tab that surfaces role-to-team drift.
 *
 * Vera access has two layers: the in-app role assignment (what modules a
 * user SEES) and Dataverse team membership (what data they can READ/WRITE).
 * The /admin role toggles keep both in sync, but drift happens (grants made
 * before a team existed, failed team adds, direct inserts). A drifted user
 * sees the module UI but every save silently fails with a missing-privilege
 * error.
 *
 * This view runs the same check as scripts/audit-vera-team-sync.ts against
 * the currently selected environment, lists drifted users, and repairs them
 * with the same team-add call the role toggles use. Fixes take effect on the
 * user's next request, no re-login needed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  SearchRegular,
  WrenchRegular,
} from '@fluentui/react-icons';
import {
  auditVeraTeamSync,
  addUserToVeraTeam,
  auditRolePrivileges,
  type TeamSyncAuditResult,
  type TeamSyncDrift,
  type RolePrivilegeAuditResult,
} from '@shared/entities/user-role-assignment';
import { useOrgContext } from '@shared/providers';

const useStyles = makeStyles({
  card: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  intro: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '760px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: tokens.colorNeutralForeground3,
  },
  td: {
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    verticalAlign: 'top',
  },
  upn: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  rowError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '12px',
    marginTop: '4px',
  },
});

type RowStatus =
  | { state: 'idle' }
  | { state: 'fixing' }
  | { state: 'fixed' }
  | { state: 'failed'; message: string };

export function AccessHealthView() {
  const styles = useStyles();
  const { orgLabel } = useOrgContext();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TeamSyncAuditResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [rowStatus, setRowStatus] = useState<Map<string, RowStatus>>(new Map());
  const [fixingAll, setFixingAll] = useState(false);
  const [userQuery, setUserQuery] = useState('');

  // Role-privilege audit runs alongside the membership audit but fails
  // independently (it needs prvReadRole, which older environments may lack).
  const [roleResult, setRoleResult] = useState<RolePrivilegeAuditResult | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [roleRunning, setRoleRunning] = useState(false);

  const setStatus = (oid: string, status: RowStatus) =>
    setRowStatus((prev) => new Map(prev).set(oid, status));

  const runCheck = async () => {
    setRunning(true);
    setRunError(null);
    setRowStatus(new Map());
    setRoleRunning(true);
    setRoleError(null);
    void auditRolePrivileges()
      .then(setRoleResult)
      .catch((err: unknown) => {
        setRoleResult(null);
        setRoleError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setRoleRunning(false));
    try {
      setResult(await auditVeraTeamSync());
    } catch (err) {
      setResult(null);
      setRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  // Auto-run when the tab opens — the whole point is admins seeing problems
  // without having to know to ask.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    void runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matchedUsers = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q || !result) return [];
    return result.users
      .filter((u) => u.fullname.toLowerCase().includes(q) || u.upn.toLowerCase().includes(q))
      .slice(0, 5);
  }, [userQuery, result]);

  const fixUser = async (user: TeamSyncDrift): Promise<boolean> => {
    if (!user.systemuserid) return false;
    setStatus(user.oid, { state: 'fixing' });
    try {
      for (const team of user.missingTeams) {
        await addUserToVeraTeam(team, user.systemuserid);
      }
      setStatus(user.oid, { state: 'fixed' });
      return true;
    } catch (err) {
      setStatus(user.oid, { state: 'failed', message: err instanceof Error ? err.message : String(err) });
      return false;
    }
  };

  const fixAll = async () => {
    if (!result) return;
    setFixingAll(true);
    try {
      for (const user of result.drift) {
        if (user.systemuserid && !user.disabled && rowStatus.get(user.oid)?.state !== 'fixed') {
          await fixUser(user);
        }
      }
    } finally {
      setFixingAll(false);
    }
  };

  const fixableCount = result
    ? result.drift.filter((d) => d.systemuserid && !d.disabled && rowStatus.get(d.oid)?.state !== 'fixed').length
    : 0;

  return (
    <Card className={styles.card}>
      <Subtitle2>
        Access health check
        <Badge appearance="outline" style={{ marginLeft: '10px' }}>
          environment: {orgLabel}
        </Badge>
      </Subtitle2>
      <Body1 className={styles.intro}>
        Two checks against the <b>{orgLabel}</b> environment: (1) every user&apos;s granted Vera
        roles vs their Dataverse team memberships (a user missing a team can open module pages
        but their edits fail to save; fixing is immediate, no re-login), and (2) each Vera
        security role&apos;s privileges vs the expected access matrix. Dev and prod drift
        independently: switch environments in your user menu and check both.
      </Body1>

      <div className={styles.actions}>
        <Button appearance="primary" icon={<ArrowSyncRegular />} onClick={() => void runCheck()} disabled={running || fixingAll}>
          {running ? 'Checking…' : result ? 'Re-run check' : 'Run check'}
        </Button>
        {result && fixableCount > 0 && (
          <Button icon={<WrenchRegular />} onClick={() => void fixAll()} disabled={fixingAll || running}>
            {fixingAll ? 'Fixing…' : `Fix all (${fixableCount})`}
          </Button>
        )}
        {running && <Spinner size="tiny" />}
      </div>

      {runError && (
        <MessageBar intent="error">
          <MessageBarBody>
            Check failed: {runError}
            {/privilege|prv[A-Z]/.test(runError)
              ? ' Your own account may be missing the team-management permissions; ask a Vera developer to re-run the security-role provisioning.'
              : ''}
          </MessageBarBody>
        </MessageBar>
      )}

      {result && result.teamsNotProvisioned.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Missing team(s) in this environment: {result.teamsNotProvisioned.join(', ')}. A Vera
            developer needs to run the team provisioning script before those can be fixed here.
          </MessageBarBody>
        </MessageBar>
      )}

      {result && result.drift.length === 0 && (
        <MessageBar intent="success">
          <MessageBarBody>
            Checked {result.checkedUsers} users. Everyone&apos;s data access matches their roles.
          </MessageBarBody>
        </MessageBar>
      )}

      {result && result.drift.length > 0 && (
        <>
          <Caption1>
            Checked {result.checkedUsers} users. {result.drift.length} with missing data access:
          </Caption1>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>User</th>
                <th className={styles.th}>Roles</th>
                <th className={styles.th}>Missing data access</th>
                <th className={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {result.drift.map((user) => {
                const status = rowStatus.get(user.oid) ?? { state: 'idle' as const };
                return (
                  <tr key={user.oid}>
                    <td className={styles.td}>
                      <div>{user.fullname}</div>
                      <div className={styles.upn}>{user.upn}</div>
                    </td>
                    <td className={styles.td}>{user.roles.join(', ')}</td>
                    <td className={styles.td}>{user.missingTeams.join(', ')}</td>
                    <td className={styles.td}>
                      {user.disabled ? (
                        <Badge appearance="outline" color="informative">
                          account disabled; can&apos;t sign in, no fix needed
                        </Badge>
                      ) : !user.systemuserid ? (
                        <Badge appearance="outline" color="informative">
                          never signed in; syncs automatically on first sign-in
                        </Badge>
                      ) : status.state === 'fixed' ? (
                        <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
                          fixed
                        </Badge>
                      ) : (
                        <>
                          <Button
                            size="small"
                            icon={status.state === 'fixing' ? <Spinner size="tiny" /> : <WrenchRegular />}
                            disabled={status.state === 'fixing' || fixingAll}
                            onClick={() => void fixUser(user)}
                          >
                            Fix
                          </Button>
                          {status.state === 'failed' && (
                            <div className={styles.rowError}>{status.message}</div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {result && (
        <>
          <Subtitle2 style={{ marginTop: '10px' }}>Check one user</Subtitle2>
          <Input
            contentBefore={<SearchRegular />}
            placeholder="Name or email..."
            value={userQuery}
            onChange={(_, d) => setUserQuery(d.value)}
            style={{ maxWidth: '360px' }}
          />
          {userQuery.trim() && matchedUsers.length === 0 && (
            <Caption1>
              No user with a Vera role matches. Users appear here only after they hold at least
              one role (grant roles on the By User tab).
            </Caption1>
          )}
          {matchedUsers.map((u) => (
            <div key={u.oid} style={{ fontSize: '13px', display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              {u.missingTeams.length === 0 ? (
                <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>in sync</Badge>
              ) : (
                <Badge appearance="filled" color="danger">missing data access</Badge>
              )}
              <b>{u.fullname}</b>
              <span className={styles.upn}>{u.upn}</span>
              <span>roles: {u.roles.join(', ')}</span>
              <span>
                teams: {u.expectedTeams.map((t) => (u.missingTeams.includes(t) ? `${t} (MISSING)` : t)).join(', ') || 'none required'}
              </span>
              {u.noSystemUser && <Caption1>never signed in; syncs on first sign-in</Caption1>}
              {u.disabled && <Caption1>account disabled</Caption1>}
            </div>
          ))}
        </>
      )}

      <Subtitle2 style={{ marginTop: '10px' }}>Security role privileges</Subtitle2>
      <Caption1>
        Each Vera role&apos;s attached Dataverse privileges compared against the expected access
        matrix. Gaps here affect <b>everyone</b> on the team that carries the role and can only
        be fixed by a developer re-running the security-role provisioning script.
      </Caption1>
      {roleRunning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Spinner size="tiny" /> <Caption1>Checking role privileges…</Caption1>
        </div>
      )}
      {roleError && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Role-privilege check unavailable: {roleError}
            {/privilege|prv[A-Z]/.test(roleError)
              ? ' (your account may lack read access to security roles; a developer can run the check with diag-role-privileges instead)'
              : ''}
          </MessageBarBody>
        </MessageBar>
      )}
      {roleResult && roleResult.healthy && (
        <MessageBar intent="success">
          <MessageBarBody>
            All Vera roles carry their expected privileges in this environment.
          </MessageBarBody>
        </MessageBar>
      )}
      {roleResult && !roleResult.healthy && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {roleResult.gaps
            .filter((g) => !g.roleFound || g.missing.length > 0)
            .map((g) => (
              <MessageBar key={g.roleName} intent="error">
                <MessageBarBody>
                  {!g.roleFound ? (
                    <>Role <b>{g.roleName}</b> does not exist in this environment. A developer needs to run the security-role provisioning script.</>
                  ) : (
                    <>
                      <b>{g.roleName}</b> is missing {g.missing.length} of {g.expectedCount} expected privileges:{' '}
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{g.missing.slice(0, 8).join(', ')}{g.missing.length > 8 ? `, and ${g.missing.length - 8} more` : ''}</span>.
                      {' '}Ask a developer to run <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>schema:provision-vera-security-roles</span> for this environment.
                    </>
                  )}
                </MessageBarBody>
              </MessageBar>
            ))}
        </div>
      )}
    </Card>
  );
}
